import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import useSWR, { mutate } from 'swr';
import { fallbackCopyTextToClipboard } from '../utils/utils';
import PaginationControls from './PaginationControls';
import {
    Alert,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    Button,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Card,
    CardContent,
    LinearProgress,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TableSkeleton, { CardSkeleton } from './TableSkeleton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePolyAuth } from '../hooks/PolyAuthContext';
import HelpText from './HelpText';


function LinkList({
    listEndpoint = '/listteamslinks/',
    onOpenMeeting,
    onSendInvite = null,
    onEditLink = null,
    onDuplicateLink = null,
    onLinkCountChange = null,
    enablePagination = false,
    enableSearch = false,
}) {
    const { t } = useTranslation('shared');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLinkId, setSelectedLinkId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState(null);
    const { api_client } = usePolyAuth();

    // Pagination and search state
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            if (searchTerm !== debouncedSearchTerm) {
                setPage(1);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Build endpoint with pagination params when enabled
    const paginatedEndpoint = enablePagination
        ? `${listEndpoint}${listEndpoint.includes('?') ? '&' : '?'}page=${page}&page_size=${pageSize}${debouncedSearchTerm ? `&search=${encodeURIComponent(debouncedSearchTerm)}` : ''}`
        : listEndpoint;

    const local_fetcher = async (url) => {
        try {
            const fetchUrl = url.startsWith('/v2') ? url : `/v2${url}`;
            const response = (await api_client.get(fetchUrl))?.data;
            if (!response) {
                throw new Error('No data found in response');
            }
            // Handle both paginated and non-paginated response shapes
            if (response.pagination) {
                return response;
            }
            const rawLinks = response.links;
            if (!rawLinks) {
                throw new Error('No links found in response');
            }
            return { links: [...rawLinks].reverse() };
        } catch (error) {
            console.log('Error fetching link data:', error);
            throw new Error('Failed to fetch data');
        }
    };


    const handleMenuOpen = (event, linkId) => {
        setAnchorEl(event.currentTarget);
        setSelectedLinkId(linkId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedLinkId(null);
    };

    const handleDeleteClick = (linkId) => {
        const link = links.find(l => l.link_uuid === linkId);
        setLinkToDelete(link);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleDeleteConfirm = () => {
        if (linkToDelete) {
            deleteLink(linkToDelete.link_uuid);
        }
        setDeleteDialogOpen(false);
        setLinkToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setLinkToDelete(null);
    };

    const { data: rawData, error, isLoading, isValidating } = useSWR(
        paginatedEndpoint,
        local_fetcher,
    );

    // Extract links and pagination from response
    const links = rawData?.links ?? null;
    const pagination = rawData?.pagination ?? null;
    const totalPages = pagination?.total_pages ?? 1;

    useEffect(() => {
        if (onLinkCountChange && !isLoading && links && !debouncedSearchTerm) {
            onLinkCountChange(pagination?.total ?? links.length);
        }
    }, [links, isLoading, onLinkCountChange, pagination, debouncedSearchTerm]);

    const deleteLink = async (link_uuid) => {
        try {
            const response = await api_client.delete(`/v2/${link_uuid}`);
            if (response.data?.status == "ok") {
                toast(t('linkList.meetingArchived'), { type: 'success' });
                mutate(paginatedEndpoint);
            }
        } catch (error) {
            console.error('Error deleting link:', error);
            toast(t('linkList.archiveFailed'), { type: 'error' });
        }
    };

    if (isLoading) {
        return isMobile
            ? <CardSkeleton count={3} />
            : <TableSkeleton columns={2} rows={5} headers={[t('linkList.tableHeaderMeeting'), t('linkList.tableHeaderActions')]} />;
    }

    if (error && !links) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {t('linkList.failedToLoadLinks')}
            </Alert>
        );
    }

    const hasNoLinks = !links || links.length === 0;

    const openMeeting = (link) => {
        onOpenMeeting(link);
    };

    const renderDisabledNotice = ({ iconSize = 16, sx = {} } = {}) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}>
            <WarningAmberIcon sx={{ fontSize: iconSize, color: theme.palette.warning.dark }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.warning.dark }}>
                {t('linkList.meetingUnavailable')}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%', overflow: 'hidden' }}>
            {enableSearch && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexShrink: 0 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={t('linkList.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                    {searchTerm !== '' && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSearchTerm('')}
                            startIcon={<CloseIcon />}
                        >
                            {t('linkList.clear')}
                        </Button>
                    )}
                </Box>
            )}
            <LinearProgress sx={{ mb: 1, flexShrink: 0, visibility: isValidating && !isLoading ? 'visible' : 'hidden' }} />
            {hasNoLinks ? (
                <Card elevation={2} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                    textAlign: 'center',
                    p: 3,
                    borderRadius: '8px'
                }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {debouncedSearchTerm ? t('linkList.noMatchingMeetings') : t('linkList.noSecureMeetingLinks')}
                    </Typography>
                    <HelpText sx={{ mb: 0 }}>
                        {debouncedSearchTerm
                            ? t('linkList.tryDifferentSearch')
                            : t('linkList.createFirstLink')}
                    </HelpText>
                </Card>
            ) : isMobile ? (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1 }}>
                    {links?.length > 0 && links.map((link) => (
                        <Card key={link.link_uuid} sx={{ mb: 2, elevation: 2 }}>
                            <CardContent sx={{ position: 'relative' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 500,
                                                ...(!link.disabled && {
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                        textDecoration: 'underline'
                                                    }
                                                })
                                            }}
                                            onClick={link.disabled ? undefined : async () => {
                                                await fallbackCopyTextToClipboard(link.url);
                                                toast.success(t('linkList.linkCopied'));
                                            }}
                                        >
                                            {link.title || t('callLogs.untitled')}
                                        </Typography>
                                        {link.disabled && renderDisabledNotice({ sx: { mt: 0.5 } })}
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, link.link_uuid)}
                                        sx={{
                                            color: 'text.secondary',
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            borderRadius: 1,
                                            ml: 1
                                        }}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => openMeeting(link)}
                                    disabled={link.disabled}
                                    fullWidth
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        whiteSpace: 'nowrap',
                                        ...(!link.disabled && {
                                            '&:hover': {
                                                backgroundColor: 'warning.light',
                                                color: 'warning.contrastText',
                                            }
                                        })
                                    }}
                                >
                                    {t('linkList.openMeeting')}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('linkList.tableHeaderMeeting')}</TableCell>
                                <TableCell align="right">{t('linkList.tableHeaderActions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {links?.length > 0 && links.map((link, index) => (
                                <TableRow key={link.link_uuid} hover>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                    {link.disabled ? (
                                        <Box sx={{ pl: 1 }}>
                                            <Typography variant="h7" sx={{ mb: 1 }}>
                                                {link.title || t('callLogs.untitled')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Button variant="text" sx={{textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start'}} onClick={async () => { await fallbackCopyTextToClipboard(link.url); toast.success(t('linkList.linkCopied')); }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h7" sx={{ mb: 1 }}>
                                                    {link.title || t('callLogs.untitled')}
                                                </Typography>
                                                <ContentCopyIcon fontSize="small" sx={{ verticalAlign: 'top' }} />
                                            </Box>
                                        </Button>
                                    )}
                                    {link.disabled && renderDisabledNotice({ iconSize: 14, sx: { pl: 1 } })}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                            {onEditLink && (
                                                <IconButton
                                                    size="small"
                                                    aria-label={t('linkList.editMeeting')}
                                                    title={t('linkList.editMeeting')}
                                                    onClick={() => onEditLink(link)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {onDuplicateLink && (
                                                <IconButton
                                                    size="small"
                                                    aria-label={t('linkList.duplicateMeeting')}
                                                    title={t('linkList.duplicateMeeting')}
                                                    onClick={() => onDuplicateLink(link)}
                                                >
                                                    <FileCopyIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<PlayArrowIcon />}
                                                onClick={() => openMeeting(link)}
                                                disabled={link.disabled}
                                                sx={{
                                                    backgroundColor: 'primary.main',
                                                    color: 'primary.contrastText',
                                                    whiteSpace: 'nowrap',
                                                    ...(!link.disabled && {
                                                        '.MuiTableRow-root:hover &': {
                                                            backgroundColor: 'warning.main',
                                                            color: 'warning.contrastText',
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'warning.light',
                                                            color: 'warning.contrastText',
                                                        }
                                                    })
                                                }}
                                            >
                                                {t('linkList.openMeeting')}
                                            </Button>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, link.link_uuid)}
                                                sx={{
                                                    color: 'text.secondary',
                                                    border: '1px solid rgba(0, 0, 0, 0.12)',
                                                    borderRadius: 1
                                                }}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}


            {enablePagination && pagination && totalPages > 1 && (
                <PaginationControls
                    page={page}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalItems={pagination.total}
                    onPageChange={setPage}
                    onPageSizeChange={(val) => { setPageSize(val); setPage(1); }}
                    itemLabel={t('linkList.itemLabel')}
                    searchTerm={debouncedSearchTerm}
                    currentPageCount={links?.length}
                />
            )}

            {/* Menu dropdown */}
            {(() => {
                const selectedLink = selectedLinkId ? links.find(link => link.link_uuid === selectedLinkId) : null;
                const isDisabledLink = selectedLink?.disabled;
                return (
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        minWidth: 160,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                    }
                }}
            >
                <MenuItem
                    disabled={isDisabledLink}
                    onClick={() => {
                        if (selectedLinkId) {
                            const selectedLink = links.find(link => link.link_uuid === selectedLinkId);
                            if (selectedLink) {
                                console.log('Copying selectedLink url', selectedLink.url);
                                fallbackCopyTextToClipboard(selectedLink.url);
                                toast(t('linkList.linkCopied'), { type: 'success' });
                            }
                            handleMenuClose();
                        }
                    }}
                    sx={{
                        py: 1.5,
                        px: 2,
                        fontSize: '0.875rem',
                        '&:hover': {
                            backgroundColor: 'rgba(64, 119, 150, 0.05)' // Theme Color 1 with lower opacity
                        }
                    }}
                >
                    <ContentCopyIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                    {t('linkList.copyMeetingLink')}
                </MenuItem>
                {onEditLink && (
                    <MenuItem
                        onClick={() => {
                            if (selectedLink) onEditLink(selectedLink);
                            handleMenuClose();
                        }}
                        sx={{ py: 1.5, px: 2, fontSize: '0.875rem' }}
                    >
                        <EditIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        {t('linkList.editMeeting')}
                    </MenuItem>
                )}
                {onDuplicateLink && (
                    <MenuItem
                        onClick={() => {
                            if (selectedLink) onDuplicateLink(selectedLink);
                            handleMenuClose();
                        }}
                        sx={{ py: 1.5, px: 2, fontSize: '0.875rem' }}
                    >
                        <FileCopyIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        {t('linkList.duplicateMeeting')}
                    </MenuItem>
                )}
                {onSendInvite && (
                    <MenuItem
                        disabled={isDisabledLink}
                        onClick={() => {
                            if (selectedLinkId) {
                                const selectedLink = links.find(link => link.link_uuid === selectedLinkId);
                                if (selectedLink) {
                                    onSendInvite(selectedLink);
                                }
                            }
                            handleMenuClose();
                        }}
                        sx={{
                            py: 1.5,
                            px: 2,
                            fontSize: '0.875rem',
                            '&:hover': {
                                backgroundColor: 'rgba(64, 119, 150, 0.05)'
                            }
                        }}
                    >
                        <EmailIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        {t('linkList.sendAnInvite')}
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        if (selectedLinkId) {
                            handleDeleteClick(selectedLinkId);
                        }
                    }}
                    sx={{
                        py: 1.5,
                        px: 2,
                        fontSize: '0.875rem',
                        color: '#855032', // Theme Color 2
                        '&:hover': {
                            backgroundColor: 'rgba(133, 80, 50, 0.05)' // Theme Color 2 with lower opacity
                        }
                    }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
                    {t('linkList.archiveMeeting')}
                </MenuItem>
            </Menu>
            );
            })()}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    {t('linkList.archiveMeeting')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {t('linkList.archiveConfirm', { title: linkToDelete?.title || t('callLogs.untitled') })}
                        {' '}{t('linkList.archiveWarning')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        {t('linkList.archive')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LinkList;